"use client";

import Link from "next/link";
import { useEffect } from "react";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { X, ArrowRight, ExternalLink } from "lucide-react";
import { company } from "@/content/company";
import { OpenSignupButton } from "@/components/popups/OpenSignupButton";
import { WhatsAppIcon } from "@/components/ui/SocialIcons";
import { getPrimaryNav } from "@/lib/site-menu";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { localizedHref, useLocale } from "@/i18n/locale";

const listVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, x: 24 },
  show: { opacity: 1, x: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
};

export function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const locale = useLocale();
  const primaryNav = getPrimaryNav().map((link) => ({
    ...link,
    href: localizedHref(link.href, locale),
  }));

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            type="button"
            aria-label="Fechar menu"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[85] bg-ink-black/60 backdrop-blur-sm lg:hidden"
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-y-0 right-0 z-[85] flex w-full max-w-sm flex-col overflow-y-auto border-l border-line bg-bg-base shadow-2xl shadow-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-menu-title"
          >
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <span id="mobile-menu-title" className="font-mono text-sm uppercase tracking-widest text-text-primary">
                Menu
              </span>
              <motion.button
                type="button"
                onClick={onClose}
                aria-label="Fechar menu"
                whileTap={{ scale: 0.9 }}
                className="rounded-md p-2 text-text-secondary transition-colors hover:text-text-primary"
              >
                <X className="h-6 w-6" />
              </motion.button>
            </div>

            <motion.nav variants={listVariants} initial="hidden" animate="show" className="flex-1 px-5 py-4">
              <ul className="space-y-1">
                {primaryNav.map((link) => {
                  if (link.highlight) {
                    return (
                      <motion.li key={link.href} variants={itemVariants} className="py-2">
                        <Link
                          href={link.href}
                          onClick={onClose}
                          className="group flex items-center gap-2.5 rounded-lg border border-accent-lime/50 bg-accent-lime/10 px-4 py-3 text-lg font-semibold text-accent-lime transition-colors hover:bg-accent-lime/20"
                        >
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-lime opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-lime" />
                          </span>
                          {link.label}
                          <ArrowRight className="ml-auto h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </motion.li>
                    );
                  }

                  if (link.external) {
                    return (
                      <motion.li key={link.href} variants={itemVariants} className="border-b border-line/60 py-1">
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center justify-between py-3 text-lg font-medium text-text-primary transition-colors hover:text-accent-lime"
                        >
                          {link.label}
                          <ExternalLink className="h-4 w-4 text-text-muted transition-colors group-hover:text-accent-lime" />
                        </a>
                      </motion.li>
                    );
                  }

                  return (
                    <motion.li key={link.href} variants={itemVariants} className="border-b border-line/60 py-1">
                      <Link
                        href={link.href}
                        onClick={onClose}
                        className="group flex items-center justify-between py-3 text-lg font-medium text-text-primary transition-colors hover:text-accent-lime"
                      >
                        {link.label}
                        <ArrowRight className="h-4 w-4 text-text-muted opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100" />
                      </Link>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.nav>

            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="show"
              transition={{ delay: 0.3 }}
              className="space-y-3 border-t border-line px-5 py-5"
            >
              <LanguageSwitcher mobile />
              <OpenSignupButton className="w-full" onClick={onClose}>
                Solicitar diagnóstico
              </OpenSignupButton>
              <a
                href={company.whatsapp.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 text-center text-sm text-text-secondary transition-colors hover:text-accent-lime"
              >
                <WhatsAppIcon className="h-4 w-4" /> Falar no WhatsApp
              </a>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
